import { useState } from "react";
import { toast } from "react-toastify";
import { MdDelete } from "react-icons/md";
import { useParams } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import DataTable from "react-data-table-component";
import Button from "../../atoms/Button";
import NoData from "../../atoms/NoData";
import Modal from "../../molecules/Modal";
import ModalConfirm from "../../molecules/ModalConfirm";
import useModal from "../../../hooks/useModal";
import { GET_FILE } from "../../../api/apiFiles";
import { UPDATE_JUICIO } from "../../../api/apiJuicio";
import { UPDATE_RECURSO } from "../../../api/apiRecurso";
import { UPDATE_FISCALIZACION } from "../../../api/apiFiscalizacion";

const DFTableDocs = ({ data, loading, etapaId, refetch, user }) => {
    const { id, etapa } = useParams();
    const { open, isOpen, close } = useModal();
    const { open: openDelete, isOpen: isOpenDelete, close: closeDelete } = useModal();
    const [loadingView, setLoadingView] = useState(false);
    const [documento, setDocumento] = useState();
    const [toDeletePk, setToDeletePk] = useState(null);

    const sortedData = [...(data ?? [])].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

    const updateEtapaFiles = async (payload) => {
        switch (etapa) {
            case "fiscalizacion":
                return await UPDATE_FISCALIZACION(etapaId, payload);
            case "juicio":
                return await UPDATE_JUICIO(etapaId, payload);
            case "recurso":
                return await UPDATE_RECURSO(etapaId, payload);
            default:
                throw new Error(`Etapa no soportada: ${etapa}`);
        }
    };

    const columns = [
        {
            name: "Documento",
            cell: row => (
                <div>
                    <p> <strong> {row.docName} </strong> &nbsp;{row.fecha} </p>
                    <p className="text-sm text-gray-700"> Actualizado por: {row.user || null} </p>
                    <p className="text-sm text-gray-500"> {row.comentarios || null} </p>
                </div>
            )
        },
        {
            name: "Archivos",
            cell: row => {
                const pairs =
                    (row.rules ?? []).flatMap(rule => {
                        const names = rule?.filenames ?? [];
                        const keys = rule?.s3Keys ?? [];
                        return names.map((name, idx) => ({
                            label: name,
                            s3Key: keys[idx] ?? name,
                        }));
                    });

                if (!pairs.length) return <span className="text-gray-400">—</span>;

                return (
                    <div className="flex flex-wrap gap-2">
                        {pairs.map((p, i) => (
                            <button
                                key={`${p.s3Key}-${i}`}
                                onClick={() => handleOpenDocument(p.s3Key)}
                                className="px-2 py-1 rounded bg-purple-800 text-white font-bold hover:bg-purple-700 active:scale-95"
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                );
            }
        },
        { name:"Acciones", width:"90px",
            cell: row => ( user && user?.role !== "Client" &&
            <Button
                text={<MdDelete size={22} />}
                width="w-10"
                bgColor="red"
                onClick={() => {
                    setToDeletePk(row?.primaryKey);
                    openDelete();
                }}
            />
        )},
    ];

    const handleDelete = async () => {
        if (!toDeletePk) return;
        try {
            const filtered = (sortedData ?? []).filter(item => item.primaryKey !== toDeletePk);
            await updateEtapaFiles({ files: filtered });
            toast.success("Registro eliminado");
            closeDelete();
            setToDeletePk(null);
            await refetch();
        } catch (err) {
            console.error(err);
            toast.error("No se pudo eliminar");
        }
    };

    const handleOpenDocument = async(s3Key) => {
        try {
            setLoadingView(true);
            open();
            const response = await GET_FILE(id, {s3Key});
            setDocumento(response);
        } catch (error) {
            console.error(error);
            toast.error("Error al obtener el archivo");
        } finally {
            setLoadingView(false);
        }
    };

    return (
        <>
            <DataTable
                data={sortedData}
                columns={columns}
                noDataComponent={<NoData/>}
                progressComponent={<PropagateLoader color="red" />}
                progressPending={loading}
            />
            {isOpen &&
                <Modal title="Previsualización del Documento" onClose={close}>
                    <div className="w-full h-[80vh] flex justify-center items-center">
                        {loadingView ?
                            <PropagateLoader color="red" />
                        :
                            <iframe src={documento} className="h-full w-full" title="Documento Previsualizado" />
                        }
                    </div>
                </Modal>
            }
            {isOpenDelete &&
                <ModalConfirm onConfirm={handleDelete} onClose={closeDelete}/>
            }
        </>
    );
};

export default DFTableDocs;